import EventEmitter from 'eventemitter3';

import { REGISTERED_LEVER_VALUES } from 'Lib/ConfigService/levers';

import {
  CONFIG_SERVICE_READ,
  CONFIG_SERVICE_RECEIVE,
  LEVER_PID_FALLBACK,
  LEVER_PID_OVERRIDE,
  SCHEDULED_UPDATE_TIMEOUT_MS,
} from './constants';
import type {
  ConfigServiceLogFunction,
  ConfigServiceStore,
  ExtractLeverValueType,
  ExtractLeverValueTypeName,
  LeverConfig,
  LeverConfigPid,
  LeverConfigTuple,
  LeverKey,
} from './types';

export default class ConfigService {
  _store: ConfigServiceStore;

  _log: ConfigServiceLogFunction;

  _emitter: EventEmitter;

  _lastReadValue: { [key: string]: (string | boolean | number)[] };

  _scheduledTimeout: { [key: string]: ReturnType<typeof setTimeout> };

  constructor(store: ConfigServiceStore, log: ConfigServiceLogFunction) {
    this._store = store;
    this._log = log;
    this._emitter = new EventEmitter();
    this._lastReadValue = {};
    this._scheduledTimeout = {};
  }

  // eslint-disable-next-line camelcase
  DEVTOOLS_setOverride<T extends LeverKey>(key: T, value?: ExtractLeverValueType<T> | null) {
    this._store.setOverride(key, value);
    this._scheduleUpdate(key);
  }

  // eslint-disable-next-line camelcase
  DEVTOOLS_getLeverValues<T extends LeverKey>(
    key: T
  ): {
    cached: ExtractLeverValueType<T> | undefined | null;
    fallback: ExtractLeverValueType<T>;
    override: ExtractLeverValueType<T> | undefined | null;
    pid: LeverConfigPid | undefined | null;
    stored: ExtractLeverValueType<T> | undefined | null;
    value: ExtractLeverValueType<T>;
  } {
    const override = this._store.getOverride(key);
    const [cached] = this._store.getCached(key) || [];
    const [stored] = this._store.getStored(key) || [];
    const fallback = this._store.getFallback(key);

    const [value, pid] = this._getValue(key);

    return {
      override,
      cached,
      stored,
      fallback,
      value,
      pid,
    };
  }

  _clearLastRead<T extends LeverKey>(key: T): void {
    delete this._lastReadValue[key];
  }

  _matchesLastRead<T extends LeverKey>(
    key: T,
    value: ExtractLeverValueType<T>,
    pid: LeverConfigPid
  ): boolean {
    if (this._lastReadValue[key]) {
      const [lastValue, lastPid] = this._lastReadValue[key];
      return lastValue === value && lastPid === pid;
    }
    return false;
  }

  _getValue<T extends LeverKey>(key: T): LeverConfigTuple<T> {
    const override = this._store.getOverride(key);
    if (override != null) {
      return [override, LEVER_PID_OVERRIDE];
    }
    const cached = this._store.getCached(key);
    if (cached) {
      return cached;
    }
    const stored = this._store.getStored(key);
    if (stored) {
      return stored;
    }
    const fallback = this._store.getFallback(key);
    return [fallback, LEVER_PID_FALLBACK];
  }

  _scheduleUpdate<T extends LeverKey>(key: T) {
    clearTimeout(this._scheduledTimeout[key]);
    this._scheduledTimeout[key] = setTimeout(() => {
      this._emitter.emit(`change:${key}`, key);
    }, SCHEDULED_UPDATE_TIMEOUT_MS);
  }

  getAllLeverValues(): Partial<{ [TLeverKey in LeverKey]: ExtractLeverValueType<TLeverKey> }> {
    const levers = {};
    Object.keys(REGISTERED_LEVER_VALUES).forEach((key) => {
      // using _getValue for internal dev reads to avoid triggering the caching logic or polluting the analytics events
      /** TODO: REMOVE-TYPE-ASSERTION - requires explicit typing of {@link REGISTERED_LEVER_VALUES}. */
      [levers[key]] = this._getValue(key as LeverKey);
    });
    return levers;
  }

  readLever<T extends LeverKey>(key: T): ExtractLeverValueType<T> {
    const [value, pid] = this._getValue(key);
    if (!this._matchesLastRead(key, value, pid)) {
      this._log(CONFIG_SERVICE_READ, { key, value, pid });
    }
    this._lastReadValue[key] = [value, pid];
    return value;
  }

  readLockLever<T extends LeverKey>(
    key: T
  ): [ExtractLeverValueType<T>, Function, string | undefined | null] {
    const [value, pid] = this._getValue(key);

    // Fallback values bypass the cache and locking behavior
    if (pid === LEVER_PID_FALLBACK || pid === LEVER_PID_OVERRIDE) {
      if (!this._matchesLastRead(key, value, pid)) {
        this._log(CONFIG_SERVICE_READ, { key, value, pid });
      }
      this._lastReadValue[key] = [value, pid];
      return [value, () => {}, null];
    }

    const hasLock = this._store.hasLock(key);
    if (!hasLock) {
      this._store.setCached(key, value, pid);
    }
    const lockId = this._store.addLock(key);
    let unlocked = false;
    const removeLockFn = () => {
      if (!unlocked) {
        this.unlockLever(key, lockId);
        unlocked = true;
      }
    };
    if (!this._matchesLastRead(key, value, pid)) {
      this._log(CONFIG_SERVICE_READ, { key, value, pid });
    }
    this._lastReadValue[key] = [value, pid];
    return [value, removeLockFn, lockId];
  }

  unlockLever<T extends LeverKey>(key: T, lockId: string) {
    const [prevValue, prevPid] = this._getValue(key);
    this._store.removeLock(key, lockId);
    if (!this._store.hasLock(key)) {
      this._clearLastRead(key);
      this._store.unsetCached(key);
      // Notify subscribers if there is a new value available as a result of unlocking the lever.
      const [value, pid] = this._getValue(key);
      if (prevValue !== value || prevPid !== pid) {
        // We defer publishing to avoid thrash and deadlock if there are multiple subscribers.
        this._scheduleUpdate(key);
      }
    }
  }

  subscribeLever<T extends LeverKey>(key: T, callback: () => void) {
    const eventName = `change:${key}`;
    this._emitter.on(eventName, callback);
    const unsubscribeFn = () => {
      this._emitter.removeListener(eventName, callback);
    };
    return unsubscribeFn;
  }

  registerLever<T extends LeverKey>(
    key: T,
    value: ExtractLeverValueType<T>,
    type: ExtractLeverValueTypeName<T>
  ) {
    this._store.setFallback(key, value, type);
  }

  receiveLevers(levers: { [T in LeverKey]: LeverConfig<T> }) {
    const leverKeys = Object.keys(levers) as LeverKey[];

    leverKeys.forEach(<T extends LeverKey>(key: T) => {
      const { value, pid, type } = levers[key];
      this._log(CONFIG_SERVICE_RECEIVE, { key, value, pid });
      this._clearLastRead(key);
      this._store.setStored(key, value, pid, type);
      this._emitter.emit(`change:${key}`, key);
    });
  }
}
